import { Errors } from '@equifinance/shared';

export type CreditAddress = {
    id: string,
    subBuilding: string,
    houseName: string,
    houseNumber: string,
    street1: string,
    street2: string,
    district: string,
    poBox: string,
    postTown: string,
    county: string,
    postcode: string,
    country: string,
    isCurrent: boolean,
    isSecurity: boolean,
    index: number,
    label: string,
  }

export enum CreditTypes {
    NONE = 'None',
    SECURED = 'Secured',
    UNSECURED = 'Unsecured',
  }

export enum NameMatchStatusTypes{
    PRIMARY = 'A',
    SECONDARY = 'B',
    PRIMARY_ASSOCIATE_1 = 'C',
  }

export enum MortgageType {
    CapitalAndInterest = 'Capital and interest',
    PartAndPart = 'Part and part',
    InterestOnly = 'Interest-only',
  }

export enum SecuredAccountType {
    FirstCharge = '1st Charge',
    SubsequentCharge = 'Subsequent Charge',
    OtherProperty = 'Other Property',
  }

export enum UnsecuredAccountType {
  Communication = 'Communication',
  CommunicationLoan = 'Communication Loan',
  ConsolidatedDebt = 'Consolidated Debt',
  CreditCard = 'Credit Card',
  CurrentAccount = 'Current Account',
  CouncilLoan = 'Council Loan',
  FamilyMember = 'Family Member',
  GreenDeals = 'Green Deals',
  HirePurchase = 'Hire Purchase',
  HomeLending = 'Home Lending',
  Insurance = 'Insurance',
  Loan = 'Loan',
  MailOrder = 'Mail Order',
  PaydayLoan = 'Payday Loan',
  PublicUtility = 'Public Utility',
  Rental = 'Rental',
  StoreCard = 'Store Card',
  StudentLoan = 'Student Loans',
  Subscription = 'Subscription',
  TradingAccount = 'Trading Account',
  }

export type AccountType = keyof typeof SecuredAccountType | keyof typeof UnsecuredAccountType;

export const RevolvingAccountTypes = [
  'CommunicationLoan',
  'CreditCard',
  'CurrentAccount',
  'MailOrder',
  'PublicUtility',
  'StoreCard',
  'TradingAccount',
];

export const NonRevolvingAccountTypes = [
  'Communication',
  'ConsolidatedDebt',
  'CouncilLoan',
  'FamilyMember',
  'GreenDeals',
  'HirePurchase',
  'HomeLending',
  'Insurance',
  'Loan',
  'PaydayLoan',
  'Rental',
  'StudentLoan',
  'Subscription',
];

export enum ConsolidationType {
    NoConsolidation = 'None',
    Arrears = 'Arrears',
    Partial = 'Partial',
    Full = 'Full',
  }

export enum PaymentShouldBePaidToType {
    Lender = 'Lender',
    ThirdParty = 'Third party',
    Applicants = 'Applicant',
    CreditorViaApplicant = 'Creditor via Applicant',
  }

export enum RepaymentVehicleType {
      SaleOfProperty = 'Sale of property',
      MoveToRented = 'Move to rented',
      Downsize = 'Downsize',
      Other = 'Other',
  }

export enum IgnoredByType {
  Broker = 'Broker',
  System = 'System',
  SalesSupport = 'SalesSupport',
  Underwriter = 'Underwriter',
}

export enum IgnoredReasonType {
  NotApplicant = 'NotApplicant',
  ExpiringCredit = 'ExpiringCredit',
  Duplicate = 'Duplicate'
}

export enum BureauCreditItemType {
  BankDefaultAgreement = 'Bank Default',
  BasicBankAccount = 'Basic Bank Account',
  BridgingFinance = 'Bridging Finance',
  BudgetAccount = 'Budget Card/Revolving Credit',
  BuyToLetMortgage = 'Buy to Let Mortgage',
  ChargeCard = 'Charge Card',
  CommsSupplyAccount = 'Communications Supplier',
  ConsolidatedDebt = 'Consolidated Debt',
  CouncilArrears = 'Council Arrears',
  CreditCard = 'Credit Card/Store Card',
  CurrentAccount = 'Current Account',
  FixedTermAgreement = 'Fixed Term',
  GreenDeals = 'Green Deals',
  HirePurchase = 'Hire Purchase',
  HomeLendingAgreement = 'Home Lending',
  InsuranceAgreement = 'Insurance',
  LocalAuthorityHousing = 'Local Authority Housing/Rental',
  MailOrderAccount = 'Mail Order Account (Cash/Direct/Normal)',
  OptionAccount = 'Option Account',
  PayDayOrShortTermLoan = 'Pay Day/Short Term Loan',
  PropertyRental = 'Property Rental',
  PublicUtilityAccount = 'Public Utility',
  RentalAgreement = 'Rental Agreement',
  SecuredLoan = 'Secured Loan',
  SocialHousingRental = 'Social Housing Rental',
  StudentLoan = 'Student Loan',
  UnpresentableCheque = 'Bounced Cheque',
  UnsecuredLoan = 'Unsecured Loan',
  XmasClub = 'Xmas Club',
}

export enum PaymentFrequency {
  Unknown = 'Unknown',
  Annually = 'Annualy',
  Fortnightly = 'Fortnightly',
  Monthly = 'Monthly',
  Periodically = 'Periodically',
  Quarterly = 'Quarterly',
  Weekly = 'Weekly'
}

export const IgnoredReasonDescriptions = {
  [IgnoredReasonType.ExpiringCredit]: 'Credit already repaid',
  [IgnoredReasonType.NotApplicant]: 'Not the applicant',
  [IgnoredReasonType.Duplicate]: 'Duplicate record',
};

export const IgnoredReasonTags = {
  [IgnoredReasonType.ExpiringCredit]: 'Expiring',
  [IgnoredReasonType.NotApplicant]: 'Not App',
  [IgnoredReasonType.Duplicate]: 'Duplicate',
};

export const NameMatchStatusTypeDescriptions = {
  [NameMatchStatusTypes.PRIMARY]: 'Subject 1 Details (including aliases)',
  [NameMatchStatusTypes.SECONDARY]: 'Subject 2 Details (including aliases)',
  C: 'Associate 1 of Subject 1 Details (including aliases)',
  D: 'Associate 2 of Subject 1 details',
  E: 'Associate 3 of Subject 1 details',
  F: 'Associate 1 of Subject 2 Details (including aliases)',
  G: 'Associate 2 of Subject 2 details',
  H: 'Associate 3 of Subject 2 details',
  I: 'Potential Associate 1 of Subject 1 details and family',
  J: 'Potential Associate 2 of Subject 1 details and family',
  K: 'Potential Associate 3 of Subject 1 details and family',
  L: 'Potential Associate 1 of Subject 2 details and family',
  M: 'Potential Associate 2 of Subject 2 details and family',
  N: 'Potential Associate 3 of Subject 2 details and family',
  P: 'Potential Associate 1 of subject 1 details',
  Q: 'Potential Associate 2 of subject 1 details',
  R: 'Potential Associate 3 of subject 1 details',
  S: 'Potential Associate 1 of subject 2 details',
  T: 'Potential Associate 2 of subject 2 details',
  U: 'Potential Associate 3 of subject 2 details',
  V: 'Family Member of Subject 1',
  W: 'Family Member of Subject 2',
  X: 'Attributable Data Subject 1 and family',
  Y: 'Attributable Data Subject 2 and family',
  Z: 'Other Person',
};

export type PaymentHistory = {
    ageInMonths: number,
    status: string,
  }

export type NameMatchedData = {
    id: string,
    firstName: string,
    middleName: string,
    lastName: string,
    nameMatchStatus: keyof typeof NameMatchStatusTypeDescriptions,
    dateOfBirth: string,
    nameMatchDescription: string,
  }

export type IgnoreOptions = {
  canIgnoreBy: IgnoredByType,
  reason: IgnoredReasonType,
}

export type CreditItem = {
    // Shared
    openingBalance: number,
    defaultBalance: number,
    lastDelinquentDate: string,
    lastDeliquentDate: string,
    defaultDate: string,
    paymentHistory: PaymentHistory[],
    ignoreOptions: IgnoreOptions[] | null,
    canUnignore: IgnoredByType[] | null,
    id: string,
    addressId: string,
    consolidationType: keyof typeof ConsolidationType,
    isEquifax: boolean,
    dateSettled: string,
    accountType: AccountType,
    bureauCreditItemType: keyof typeof BureauCreditItemType,
    balance: number,
    monthlyPayment: number,
    paymentTerms: number,
    updateDate: string,
    startDate: string,
    endDate: string,
    paymentFrequency: keyof typeof PaymentFrequency,
    supplementaryInformation: string,
    additionalSupplementaryInformation: string,
    consolidatedAmount: number,
    otherChargeHolderName: string,
    hasContractualMonthlyPayments: boolean,
    actualBalance: number,
    actualMonthlyPayment: number,
    resultingBalance: number,
    resultingMonthlyPayment: number,
    ignored: boolean,
    mortgageType: MortgageType,
    arrearsInLastTwelveMonths: number,
    missedFullCMPLastTwelveMonths: number,
    missedFullCMPLastSixMonths: number,
    missedFullCMPLastThreeMonths: number,
    inArrangement: boolean,
    arrangementMonthlyPayment: number,
    consolidationPayeeType: keyof typeof PaymentShouldBePaidToType,
    consolidationPayeeName: string,
    consolidationAccountNumber: string,
    consolidationSortCode: string,
    consolidationReferenceNumber: string,
    nameMatchedData: NameMatchedData[],
    overallRemainingTerm: number,
    ignoredBy: keyof typeof IgnoredByType,
    ignoredReason: keyof typeof IgnoredReasonType,
    ordinal: number,
    // Secured
    applicantIds: string[],
    lender: number,
    amountOfArrears: number,
    inArrears: boolean,
    interestRate: number,
    remainingFixedTerm: number,
    isFixedRate: boolean,
    repaymentVehicle: keyof typeof RepaymentVehicleType,
    repaymentVehicleDescription: string,
    capitalBalance: number,
    interestOnlyBalance: number,
    missedFullCMPLastTwoMonths: number,
    isLender: boolean,
    // Unsecured
    creditLimit: number,
    manualCreditLimit: number,
    manualOpeningBalance: number,
    isUnderIVA: boolean,
    supplier: number,
    otherSupplierName: string,
    companyClassDescription?: string,
    bureauCreditItemTypeDescriptor?: string,
  }

export enum TypeOfJudgementType {
  CCJ = 'CCJ',
  Bankruptcy = 'Bankruptcy',
  IVA = 'IVA',
  DAS = 'DAS',
  ConsumerCredit = 'Consumer Credit',
  AdministrationOrder = 'Administration Order',
  DebtReliefOrder = 'Debt Relief Order'
}

export type Insolvency = {
    courtDetails: string,
    courtDate: string,
    equifaxType: string,
    ignoredBy: keyof typeof IgnoredByType,
    ignoredReason: keyof typeof IgnoredReasonType,
    ignored: boolean,
    ignoreOptions: IgnoreOptions[] | null,
    canUnignore: IgnoredByType | null,
    id: string,
    addressId: string,
    applicantIds: string[],
    typeOfJudgement: keyof typeof TypeOfJudgementType,
    isSatisfied: boolean,
    satisfiedDate: string,
    monthlyPayment: number,
    registeredAtLandRegistry: boolean,
    consolidationType: keyof typeof ConsolidationType,
    consolidationSortCode: string,
    consolidationReferenceNumber: string,
    consolidationAccountNumber: string,
    isEquifax: boolean,
    nameMatchedData: NameMatchedData[],
    manualCourtDate: string,
    manualValue: number,
    manualSatisfiedDate: string,
    hasSatisfiedDateFromEquifax: boolean,
    hasCourtDateFromEquifax: boolean,
    value: number,
    consolidatedAmount: number,
    consolidationPayeeType: keyof typeof PaymentShouldBePaidToType,
    consolidationPayeeName: string
    ordinal: number,
  }

type CreditItems = {
    securedCreditItems: {
        id: string,
        creditItems: Record<string, CreditItem>
    },
    unsecuredCreditItems: {
        id: string,
        creditItems: Record<string, CreditItem>
    },
}

export type InsolvencyRecords = {
    id:string,
    insolvencyRecords: Record<string, Insolvency>,
}

export type DisbursementGroup = {
  id: string,
  name: string,
  amount?: number,
  sortCode?: string,
  accountNumber?: string,
  reference?: string,
}

export type Disbursement = {
  id: string,
  name: string,
  type: string,
  amount: number,
  sortCode?: string,
  accountNumber?: string,
  reference?: string,
  groupId: string,
}

export type DisbursementSummary = {
  id: string,
  disbursementGroups: DisbursementGroup[],
  disbursements: Disbursement[],
  total: number,
}

export type Credit = CreditItems & {
    id: string,
    applicants: NameMatchedData[],
    addresses: Record<string, CreditAddress>,
    insolvencyRecords: InsolvencyRecords,
    disbursementSummary: DisbursementSummary,
    errors: Errors,
    warnings: Errors,
    productsResultDigest: string | null,
    // Internal use
    updateCount: number,
    dirtyCount: number,
    initiator: string,
  }

export type AddressTypes = 'current' | 'security' | 'other';

export const addressTypeFromAddress = (address: CreditAddress): AddressTypes => {
  if (address.isSecurity) return 'security';
  if (address.isCurrent) return 'current';
  return 'other';
};

export const CreditTypesMap = {
  [CreditTypes.NONE]: '' as keyof CreditItems,
  [CreditTypes.SECURED]: 'securedCreditItems' as keyof CreditItems,
  [CreditTypes.UNSECURED]: 'unsecuredCreditItems' as keyof CreditItems,
};

export const checkCreditItemIsSettled = (creditItem: CreditItem) => creditItem.endDate != null && !!creditItem.paymentHistory.find((history) => history.status === 'S');

export const checkCreditItemIsDefaulted = (creditItem: CreditItem) => creditItem.endDate != null && !!creditItem.paymentHistory.find((history) => history.status === 'D');

export const checkCreditItemIsOpen = (creditItem: CreditItem) => !creditItem.ignored && !checkCreditItemIsSettled(creditItem);

export const checkInsolvencyIsSettled = (insolvency: Insolvency) => insolvency.isEquifax && insolvency.satisfiedDate != null && insolvency.hasSatisfiedDateFromEquifax;

export function creditAddressSort(a: CreditAddress, b: CreditAddress) {
  if (a.isSecurity) {
    return -1;
  }
  if (a.isCurrent && !b.isSecurity) {
    return -1;
  }
  return 1;
}
